/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import {
	CustomBlock,
	ElementName,
	MetadataBlock,
	PreviewBlock,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import {
	CommonCardWrapperStyles as CommonCardWrapper,
	EmojiRenderer,
	useSmartCardEmoji,
	useTrackedSmartCardState,
} from '@atlassian/company-hub-common';
import {
	CustomImageUploadRenderer,
	getImageTypeToShow,
} from '@atlassian/company-hub-common/media-components';

import { CardSize } from '../../../../../../types';
import { CardErrorState } from '../../../error-states';
import { type CommonCardProps } from '../../../types';
import { MediumLargeCardWrapper as CardWrapper } from '../../card-component-styles';

const titleBlockUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		display: 'none',
	},
	// ensure "Connect your account" link for unauth 3p links is boosted
	// over general smart card link destination so user can connect account
	zIndex: 1,
});

const titleBlockHideUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-smart-element-group] > button': {
		display: 'none',
	},
});

const titleBlockStyles = css({
	marginTop: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
		flexShrink: 0,
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a, a:visited, a:focus, a:hover, a:active': {
		textDecoration: 'none',
		pointerEvents: 'auto',
	},
});

const customImageUploadStyles = css({
	marginLeft: 'calc(var(--container-gap-left) * -1)',
	marginRight: 'calc(var(--container-gap-right) * -1)',
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginTop: 'calc(var(--container-padding) * -1)',
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-of-type': {
		marginBottom: 'calc(var(--container-padding) * -1)',
	},
});

const floatingEmojiStyles = css({
	position: 'relative',
	top: token('space.negative.150'),
	bottom: token('space.025'),
	height: '0px',
	overflow: 'visible',
});

type MediumCardProps = CommonCardProps;

export const MediumCard = ({
	contentId,
	cardId,
	link,
	title,
	imageSrc,
	cardDimensions,
	isAvatarShown,
	isPublishDateShown,
	description,
	isInViewMode,
	onCardSucceeded,
	onCardFailed,
	imageAltText,
	enableSnippetRenderer,
	showDescription,
	...analyticsPassThroughProps
}: MediumCardProps) => {
	const cardHeight = cardDimensions.height!;

	const {
		errorCardStatus,
		isNoResult,
		isRestricted,
		isUnauthorized,
		trackAndCategorizeError,
		trackSuccess,
	} = useTrackedSmartCardState(cardId, link, onCardSucceeded, onCardFailed);

	const { emojiId } = useSmartCardEmoji(link);
	const { showUnsplashImage, showExternalSiteImage, showCustomUploadedImage, hasImage } =
		getImageTypeToShow({
			imageSrc,
		});

	const showCustomTitleUnauthed = isUnauthorized && !!title;
	const showCustomDescUnauthed = isUnauthorized && !!description;
	const showSnippet = (!isUnauthorized || showCustomDescUnauthed) && showDescription;

	if (isRestricted || isNoResult) {
		return (
			<CardErrorState
				isRestricted={isRestricted}
				isNoResult={isNoResult}
				link={link}
				cardId={cardId}
				cardSize={CardSize.MEDIUM}
				cardHeight={cardHeight}
				isInViewMode={isInViewMode}
				errorStatus={errorCardStatus}
				{...analyticsPassThroughProps}
			/>
		);
	}

	return (
		<CommonCardWrapper isInViewMode={isInViewMode}>
			<CardWrapper cardHeight={cardHeight} hasImage={hasImage}>
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					platform="web"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: isInViewMode,
						size: SmartLinkSize.Medium,
						enableSnippetRenderer,
					}}
					url={link}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{(showUnsplashImage || showExternalSiteImage) && (
						<PreviewBlock ignoreContainerPadding overrideUrl={imageSrc} />
					)}
					{showCustomUploadedImage && (
						<CustomBlock css={customImageUploadStyles}>
							<CustomImageUploadRenderer
								contentId={contentId}
								imageSrc={imageSrc}
								imageAltText={imageAltText}
								shouldShowErrorMessage
							/>
						</CustomBlock>
					)}
					<CustomBlock css={floatingEmojiStyles} testId="smart-block-link-card-emoji-container">
						<EmojiRenderer emojiId={emojiId} />
					</CustomBlock>
					<TitleBlock
						hideIcon
						metadata={!!isAvatarShown ? [{ name: ElementName.OwnedByGroup }] : []}
						text={title}
						anchorTarget="_self"
						css={[
							titleBlockStyles,
							showCustomTitleUnauthed && titleBlockHideUnauthorizedStyles,
							!showCustomTitleUnauthed && isUnauthorized && titleBlockUnauthorizedStyles,
						]}
					/>
					{!!isPublishDateShown && <MetadataBlock primary={[{ name: ElementName.ModifiedOn }]} />}
					{showSnippet && (
						<SnippetBlock text={description} showFooter={false} maxLines={hasImage ? 3 : 6} />
					)}
				</SmartCardSSR>
			</CardWrapper>
		</CommonCardWrapper>
	);
};
